/**
 * Created by Alican Köker on 2019-01-09.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {TimelineLite, TweenLite} from "gsap";
import {EdisonSoundConfig} from "../../../EdisonSoundConfig";

export class EdisonFreespinMeter extends BaseView {

    //DATA
    private _currentValue:number = 0;
    private _lastValue:number = 0;
    private _cap:number = 0;

    //VISUAL
    private _updateMeterTimeline:TimelineLite;
    private _blinking:boolean = false;
    private _lightSpine:PIXI.spine.Spine;
    private _color:string;
    private _lightSpineContainerSprite:PIXI.Sprite;
    private _plusOne:PIXI.Sprite;
    private _plusOneBg:PIXI.Sprite;
    private _plusOneNormalTexture:PIXI.Texture;
    private _plusOneActiveTexture:PIXI.Texture;
    private _reset:boolean = false;
    private _blinkSpeed:number = .1;
    private _blinkMeterLightTimeline:TimelineLite;
    private _blinkMeterBgTimeline:TimelineLite;
    private _orientation:Orientation;

    //red yellow purple green blue
    private _lightPositions:number[][] = [[0, 0], [0, 0], [0, 0], [0, 0], [0, 0], [0, 0]];
    private _indicatorColors:string[] = ["meterBlack", "meterWhite", "meterRed", "meterPurple", "meterBlue", "meterGreen"];
    private _meterNumberBgActiveNames:string[] = ["blackMeterNumberBgActive", "whiteMeterNumberBgActive", "redMeterNumberBgActive", "purpleMeterNumberBgActive", "blueMeterNumberBgActive", "greenMeterNumberBgActive"];
    private _meterNumberNames:string[] = ["blackMeterNumber", "whiteMeterNumber", "redMeterNumber", "purpleMeterNumber", "blueMeterNumber", "greenMeterNumber"];
    private _skinNames:string[] = ["Black", "White", "Red", "Purple", "Blue", "Green"];
    private _meterNumberActiveNames:string[] = ["blackMeterNumberActive", "whiteMeterNumberActive", "redMeterNumberActive", "purpleMeterNumberActive", "blueMeterNumberActive", "greenMeterNumberActive"];


    constructor(cap:number, color:string) {
        super(ResourcesGroupName.MAIN);

        this._color = color;
        this._cap = cap;
        this.createVisualElements();
    }

    private createVisualElements():void {
        this._lightSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("meters"));
        this._lightSpine.skeleton.setSkinByName(this._skinNames[this._indicatorColors.indexOf(this._color)]);

        this._lightSpineContainerSprite = new PIXI.Sprite();
        this._lightSpineContainerSprite.addChild(this._lightSpine);

        this._plusOneNormalTexture = GameResources.getTextures(this._meterNumberNames[this._indicatorColors.indexOf(this._color)])[0];
        this._plusOneActiveTexture = GameResources.getTextures(this._meterNumberActiveNames[this._indicatorColors.indexOf(this._color)])[0];

        this._plusOne = new PIXI.Sprite(this._plusOneNormalTexture);
        this._plusOne.anchor.set(.5);
        this._plusOne.position.set(-4, 9);
        this._plusOne.scale.set(.8);

        this._plusOneBg = new PIXI.Sprite(GameResources.getTextures(this._meterNumberBgActiveNames[this._indicatorColors.indexOf(this._color)])[0]);
        this._plusOneBg.anchor.set(.5);
        this._plusOneBg.visible = false;
        this._plusOneBg.blendMode = PIXI.BLEND_MODES.ADD;

        this.addChild(this._lightSpineContainerSprite);
        this.addChild(this._plusOneBg);
        this.addChild(this._plusOne);

        let pos:number[] = this._lightPositions[this._indicatorColors.indexOf(this._color)];
        this._lightSpine.position.set(pos[0], pos[1]);

    }

    public setDirection(orientation:Orientation):void {
        this._orientation = orientation;
        this._lightSpine.scale.set(1, 1);

        if(orientation === Orientation.LANDSCAPE) {
            if(this._color === "meterGreen" || this._color === "meterBlue" || this._color === "meterBlack") {
                this._lightSpine.scale.set(-1, 1);
            }
            if(this._color === "meterPurple" || this._color === "meterRed") {
                this._lightSpine.scale.set(.95, .95);
            }
        } else {
            if(this._color === "meterBlack" || this._color === "meterBlue" || this._color === "meterGreen") {
                this._lightSpine.scale.set(-1, 1);
            }
        }
    }

    public handlePlusOneBgPosition(orientation:Orientation):void {

        const plusOnePositionsLandscape:number[][] = [[-35, 30], [32, 30], [32, 36], [34, 32], [-35, 40], [-35, 40]];
        const plusOneBgPositionsLandscape:number[][] = [[-45, 12], [38, 10], [40, 37], [40, 37], [-45, 45], [-45, 45]];

        const plusOnePositionsPortrait:number[][] = [[-36, 27], [35, 25], [35, 36], [32, 36], [-30, 40], [-35, 37]];
        const plusOneBgPositionsPortrait:number[][] = [[-47, 13], [45, 10], [43, 39], [37, 45], [-45, 42], [-44, 43]];

        const index:number = this._indicatorColors.indexOf(this._color);

        if(orientation === Orientation.LANDSCAPE) {
            this._plusOneBg.position.set(plusOneBgPositionsLandscape[index][0], plusOneBgPositionsLandscape[index][1]);
            this._plusOne.position.set(plusOnePositionsLandscape[index][0], plusOnePositionsLandscape[index][1]);
        } else {
            this._plusOneBg.position.set(plusOneBgPositionsPortrait[index][0], plusOneBgPositionsPortrait[index][1]);
            this._plusOne.position.set(plusOnePositionsPortrait[index][0], plusOnePositionsPortrait[index][1]);
        }
    }

    public activatePlusOne():void {
        this._plusOneBg.visible = true;
        this._plusOne.texture = this._plusOneActiveTexture;
    }

    public deactivatePlusOne():void {
        this._plusOneBg.visible = false;
        this._plusOne.texture = this._plusOneNormalTexture;
        this._plusOne.scale.set(.8);
        this._plusOneBg.scale.set(1);
    }

    public updateMeter(value:number):void {
        let previousValComparison:boolean = this._currentValue === value;
        this._lastValue = this._currentValue;
        this._currentValue = value;
        if(!previousValComparison)
            this.updateMeterVisual();
    }

    public updateMeterVisual():void {
        this._plusOne.scale.set(.8);
        this._plusOneBg.scale.set(1);
        this._lightSpineContainerSprite.alpha = 1;

        this._updateMeterTimeline = new TimelineLite();

        if(this._currentValue > 0 && this._currentValue < this._cap + 1) {
            this._reset = false;
            this._updateMeterTimeline.add(() => {
                this.increaseMeter(this._currentValue - 1, this._currentValue);
                SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_METER_UPDATE);
            });

            if(this._currentValue === this._cap) {
                if(!this._blinking) {
                    SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_METER_FILLED);
                    this.blink();
                }
                this.activatePlusOne();
                this._blinkSpeed = .5;
            }

        } else {
            this._updateMeterTimeline.add(() => this._lightSpine.state.setEmptyAnimation(0, 1));
        }
    }

    private increaseMeter(from:number, to:number):void {
        let animName:string = "meter_10_countUp";

        let entry:PIXI.spine.core.TrackEntry = this._lightSpine.state.setAnimation(0, animName, false);

        let attTl:PIXI.spine.core.Timeline = entry.animation.timelines[10 + from];

        entry.animationStart = (<PIXI.spine.core.AttachmentTimeline>attTl).frames[0];

        attTl = entry.animation.timelines[10 + to];

        entry.animationEnd = (<PIXI.spine.core.AttachmentTimeline>attTl).frames[0];
    }

    public drainMeter():void {
        let animName:string = this._cap === 8 ? this._orientation === Orientation.LANDSCAPE ? "meter_8_countDown" : "meter_8_countDownPort" : this._orientation === Orientation.LANDSCAPE ? "meter_10_countDown" : "meter_10_countDownPort";

        let scaleX:number = this._lightSpine.scale.x;
        let scaleY:number = this._lightSpine.scale.y;

        let tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            TweenLite.to(this._lightSpine.scale, .1, {x : scaleX * 1.2, y : scaleY * 1.2});
        });
        tl.add(() => {
            TweenLite.to(this._lightSpine.scale, .1, {x : scaleX, y : scaleY});
        }, .1);

        tl.add(() => {
            let entry:PIXI.spine.core.TrackEntry = this._lightSpine.state.setAnimation(0, animName, false);
            entry.animationStart = 0;

            entry.animationEnd = Math.abs((this._cap + 1) / 30);

            SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_METER_DRAINED);
        }, .2);
    }

    public resetValue():void {
        this._currentValue = 0;
        this.stopBlinkingLight();
    }

    public resetMeterLight():void {
        this.resetValue();

        this.stopBlinkingBg();

        this._lightSpineContainerSprite.alpha = 1;

        this.drainMeter();

        this._blinking = false;
    }

    public get cap():number {
        return this._cap;
    }

    private blink():void {
        this._blinkMeterLightTimeline = new TimelineLite();
        this._blinkMeterLightTimeline.add(() => {
            TweenLite.to(this._lightSpineContainerSprite, .3, {alpha : .25});
        });
        this._blinkMeterLightTimeline.add(() => {
            TweenLite.to(this._lightSpineContainerSprite, .3, {alpha : 1});
            SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_METER_FLASH);
        }, this._blinkSpeed);
        this._blinkMeterLightTimeline.add(() => {
            this.blink();
        }, this._blinkSpeed * 2);
        this._blinking = true;
    }

    private stopBlinkingLight():void {
        if(this._blinkMeterLightTimeline) {
            this._blinkMeterLightTimeline.pause();
            this._blinkMeterLightTimeline.kill();
        }
    }

    private stopBlinkingBg():void {
        if(this._blinkMeterBgTimeline) {
            this._blinkMeterBgTimeline.pause();
            this._blinkMeterBgTimeline.kill();
            this._plusOneBg.visible = true;
            this._plusOneBg.alpha = 1;
        }
    }

    public playEndBlinkNumberBgSequence():void {
        let tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_METER_MOVE);
            TweenLite.to(this._plusOne.scale, .3, {x : 2, y : 2});
            TweenLite.to(this._plusOneBg.scale, .3, {x : 2.5, y : 2.5});
        });

        tl.add(() => {
            TweenLite.to(this._plusOne.scale, .2, {x : 1.3, y : 1.3});
            TweenLite.to(this._plusOneBg.scale, .2, {x : 1.5, y : 1.5});
        }, .3);

        tl.add(() => {
            TweenLite.to(this._plusOne.scale, .1, {x : 1.7, y : 1.7});
            TweenLite.to(this._plusOneBg.scale, .1, {x : 2, y : 2});
        }, .5);

        tl.add(() => {
            TweenLite.to(this._plusOne.scale, .2, {x : .8, y : .8});
            TweenLite.to(this._plusOneBg.scale, .2, {x : 1.3, y : 1.3});
        }, .9);

        tl.add(() => {
            this.deactivatePlusOne();
        }, 1)
    }
}